import { useEffect, useState } from 'preact/hooks';
import { useRouter } from 'preact-router';
import { useDispatch } from 'react-redux';

import { useLatestActiveTime } from '@/hooks/useLatestActiveDateTime';
import { useSelector } from '@/redux/helper';
import { setGuestSessionLockUntil } from '@/redux/slices/deviceSettings/actions';
import { selectIsGuestSessionLocked } from '@/redux/slices/deviceSettings/selectors';

import useOpenSpeedyFlow from '../SpeedyFlow/hooks/useOpenSpeedyFlow';
import speedyFlowSlice from '../SpeedyFlow/redux/slice';

const { closeSpeedyFlow } = speedyFlowSlice.actions;

const isEligiblePath = (path: string) => path.startsWith('/home') || path.startsWith('/highlights');

/**
 * Controls guest-session modal timing and lock enforcement at the app level.
 * - If guest is locked, opens the non-dismissible modal immediately.
 * - If not locked and on /home, arms a one-shot timer based on remoteConfig.
 */

const GuestExperienceController = () => {
  const dispatch = useDispatch();
  const logged = useSelector((state) => state.user.logged);
  const timeoutMs = useSelector((state) => state.remoteConfig.timeoutGuestUserAfterMs);
  const lockForMs = useSelector((state) => state.remoteConfig.resetGuestUserTimeoutAfterMs);
  const [now] = useLatestActiveTime();
  const isLocked = useSelector((state) => selectIsGuestSessionLocked(state, now));
  const isFlowOpen = useSelector((state) => !!state.modals.speedyFlow?.visible);
  /**
   * Whether the flow that is open is this controller's own time gate.
   *
   * Read off the flow itself rather than remembered in a ref. A ref only records that this
   * controller once opened *a* flow, and nothing clears it when someone else closes that flow — the
   * router closing it on navigation, the machine reaching its terminal, the visitor signing in.
   * After any of those the ref stays true, and the next navigation to an ineligible path closes
   * whatever flow happens to be open by then.
   *
   * That is what broke going from /home to a restricted habitat: the router closed the time gate,
   * the habitat opened its own flow, and this controller closed that one too — leaving a blurred
   * page with no modal on it until a reload.
   */
  const isTimeGateOpen = useSelector(
    (state) => !!state.modals.speedyFlow?.visible && state.modals.speedyFlow.gate?.entryPoint === 'guest-session',
  );
  const openSpeedyFlow = useOpenSpeedyFlow();

  const [{ path }] = useRouter();
  const [isTimeToLock, setIsTimeToLock] = useState(false);

  const armDelayMs = logged || isLocked ? null : timeoutMs;

  // countdown to flag lock state
  useEffect(() => {
    if (armDelayMs == null || isLocked) return undefined;
    /**
     * A fresh countdown means the previous one is spent.
     *
     * `isTimeToLock` says "the timer just fired, gate them now" — it exists because the lock is
     * read against a clock that ticks on its own schedule, so `isLocked` lags the moment the
     * timeout fires by up to one tick. Left set, it outlived the lock it belonged to: once
     * `resetGuestUserTimeoutAfterMs` expired the delay was still zero, so the visitor was re-gated
     * the instant they landed on an eligible page and never got the free window the reset exists to
     * give them back.
     */
    setIsTimeToLock(false);
    const id = setTimeout(() => {
      setIsTimeToLock(true);
      dispatch(setGuestSessionLockUntil(Date.now() + lockForMs));
    }, armDelayMs);
    return () => clearTimeout(id);
  }, [armDelayMs, lockForMs, isLocked, dispatch]);

  // open on navigation to an eligible path while locked (e.g., came from /schedule to /home)
  useEffect(() => {
    const delayMs = isLocked ? 1000 : isTimeToLock ? 0 : null;
    const eligiblePath = isEligiblePath(path ?? '');

    /**
     * Leaving the pages the gate applies to is the *only* reason this controller closes its own
     * flow. And only its own: a flow the visitor started themselves, or one the page they just
     * landed on opened, must survive.
     *
     * Signing in is deliberately not a reason, though it used to be. A visitor who signs up inside
     * the time gate is signed in the moment the account exists — and still owes a verification, in
     * the very modal this would tear down. It was closing on the first half of its own success and
     * taking the second half with it. The machine ends the flow when the gate is genuinely
     * satisfied, which for an unconfirmed address it is not.
     */
    if (isTimeGateOpen && !eligiblePath) {
      dispatch(closeSpeedyFlow());
    }

    if (logged || !eligiblePath || delayMs == null) {
      return undefined;
    }

    // Any open flow, not just this one — the visitor is already being asked something, and a second
    // modal stacked on top of it is never the answer.
    if (isFlowOpen) {
      return undefined;
    }

    const scheduledId = setTimeout(() => {
      // Persistent: this one is the time gate itself, not an offer. Escape or a backdrop click
      // would hand back the free viewing the gate exists to stop, so it closes only when this
      // controller closes it — on navigating somewhere the gate does not apply — or when the flow
      // itself decides it is done.
      openSpeedyFlow({
        kind: 'account',
        entryPoint: 'guest-session',
        persistent: true,
      });
    }, delayMs);
    return () => {
      clearTimeout(scheduledId);
    };
  }, [logged, isLocked, isFlowOpen, isTimeGateOpen, isTimeToLock, path, dispatch, openSpeedyFlow]);

  return null;
};

export default GuestExperienceController;
